/**
 * V113-7-5 — the program Operate card: monitoring coverage + a degradation rollup for the AI systems (one read:
 * GET /monitoring/systems?program=<key>, any member), for the systems in scope for that program. Mounted on every
 * program's Operate tab; it renders nothing where the program does not scope by systems (`program.applicable` false:
 * FedRAMP / GovRAMP are boundary-scoped) or has none. Reuses the Monitoring page's chips and next-action routing.
 */
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Spinner } from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { getMonitoringSummary } from '../../lib/monitoring-summary';
import { nextActionHref, pickNext, worstFirst } from '../../lib/monitoring-view';
import { monitoringErrorOf } from '../../lib/monitoring-api';
import { plural } from '../../lib/format';
import { PackChip } from './parts';
import '../../pages/today.css';
import '../../pages/monitoring.css';

export function MonitoringOperateCard({ slug, prefix, program }: { slug: string; prefix: string; program: string }) {
  const q = useQuery({
    queryKey: ['monitoring', slug, 'summary', 'program', program],
    queryFn: () => getMonitoringSummary(slug, { program }),
    enabled: !!slug, retry: false,
  });
  if (q.isLoading) return <Spinner label="Loading monitoring" />;
  // 400 unknown_program (a program the server doesn't scope) reads as "not applicable" here.
  if (q.error && monitoringErrorOf(q.error).code === 'unknown_program') return null;
  if (q.error || !q.data) return <Alert variant="error">Monitoring could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const d = q.data;
  if (d.program && !d.program.applicable) return null;
  if (!d.systems.length) return null;
  const degraded = d.systems.filter((r) => r.open_monitoring_findings.by_kind.control_degradation);
  const overdue = d.systems.filter((r) => r.open_monitoring_findings.by_kind.monitoring_testing_overdue);
  const next = pickNext(d.systems);
  const worst = worstFirst(d.systems).filter((r) => r.pack_state && r.pack_state !== 'current').slice(0, 3);
  return (
    <Card density="compact" className="mon-operate" headingId="mon-op-h" title="Monitoring" right={<PackChip state={d.pack?.status ?? null} />}>
      <dl className="mon-operate-stats">
        <div><dt>Controls passing</dt><dd>{d.tiles.passing} of {d.tiles.of}</dd></div>
        <div><dt>Stopped passing</dt><dd>{plural(degraded.length, 'system')}</dd></div>
        <div><dt>Checks overdue</dt><dd>{plural(overdue.length, 'system')}</dd></div>
        <div><dt>Unmonitored items</dt><dd>{d.tiles.unmonitored_components}</dd></div>
      </dl>
      {worst.length ? (
        <ul className="mon-rows" aria-label="Systems that need attention">
          {worst.map((r) => (
            <li key={r.system_id} className="mon-row">
              <span className="mon-row-main"><span className="mon-name">{r.name}</span><span className="mon-sub">{r.passing} of {r.of} passing · {plural(r.open_issues, 'open issue')}</span></span>
              <span className="mon-row-acts"><PackChip state={r.pack_state} /></span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mon-actions">
        {next?.next_action ? <Link className="gp-linkbtn gp-linkbtn--compact" to={nextActionHref(prefix, next.next_action)}>{next.name}: {next.next_action.label}</Link> : null}
        <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/monitoring`}>Open Monitoring</Link>
      </div>
    </Card>
  );
}
